import { useEffect, useState } from 'react';
import { Star, Loader2 } from 'lucide-react';
import { adminApi } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';

export default function TestimonialsSettingsPage() {
  const { hasPermission } = useAuth();
  const canManageSettings = hasPermission('all');
  const backendBase = process.env.REACT_APP_BACKEND_URL || '';

  const [testimonials, setTestimonials] = useState([]);
  const [testimonialsLoading, setTestimonialsLoading] = useState(true);
  const [testimonialUpdatingId, setTestimonialUpdatingId] = useState('');

  const getAssetUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http://') || url.startsWith('https://')) return url;
    if (url.startsWith('/assets') && backendBase) return `${backendBase}${url}`;
    return url;
  };

  const fetchTestimonials = async () => {
    setTestimonialsLoading(true);
    try {
      const response = await adminApi.getTestimonials({ page_size: 200 });
      setTestimonials(response.data?.items || []);
    } catch (error) {
      toast.error('Failed to load testimonials');
    } finally {
      setTestimonialsLoading(false);
    }
  };

  useEffect(() => {
    if (!canManageSettings) return;
    fetchTestimonials();
  }, [canManageSettings]);

  const updateTestimonialFlag = async (testimonialId, payload) => {
    setTestimonialUpdatingId(testimonialId);
    try {
      await adminApi.updateTestimonial(testimonialId, payload);
      setTestimonials((prev) =>
        prev.map((item) => (item.id === testimonialId ? { ...item, ...payload } : item))
      );
      toast.success('Testimonial updated');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update testimonial');
    } finally {
      setTestimonialUpdatingId('');
    }
  };

  if (!canManageSettings) {
    return null;
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-[#2D2420]">Testimonials</h1>
        <p className="text-[#A8A8A8] mt-1">Review customer testimonials and mark featured testimonials for website display</p>
      </div>

      <Card className="bg-white border-[#E5E5E5]">
        <CardHeader>
          <CardTitle className="text-lg text-[#2D2420] flex items-center gap-2">
            <Star className="w-5 h-5 text-[#C97A5E]" /> Testimonials
          </CardTitle>
          <CardDescription>Review customer testimonials and mark featured testimonials for website display.</CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {testimonialsLoading ? (
            <div className="flex items-center justify-center h-32">
              <Loader2 className="w-6 h-6 animate-spin text-[#C97A5E]" />
            </div>
          ) : testimonials.length === 0 ? (
            <div className="flex items-center justify-center h-32 text-[#A8A8A8]">
              No testimonials yet
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-[#FAFAF9] border-b border-[#E5E5E5]">
                  <tr>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Customer</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Review</th>
                    <th className="text-center py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Rating</th>
                    <th className="text-center py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Approved</th>
                    <th className="text-center py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Featured</th>
                    <th className="text-center py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Active</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#E5E5E5]">
                  {testimonials.map((item) => (
                    <tr key={item.id} className="hover:bg-[#FAFAF9]/50">
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-3">
                          <div className="w-11 h-11 rounded-full overflow-hidden bg-[#F2ECE8] flex items-center justify-center text-[#5C4033] text-xs font-semibold">
                            {item.photo_url ? (
                              <img
                                src={getAssetUrl(item.photo_url)}
                                alt={item.name || 'Customer'}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              (item.name || '?').slice(0, 1).toUpperCase()
                            )}
                          </div>
                          <div>
                            <p className="text-sm font-semibold text-[#2D2420]">{item.name || '-'}</p>
                            <p className="text-xs text-[#A8A8A8]">{item.email || '-'}</p>
                          </div>
                        </div>
                      </td>
                      <td className="py-3 px-4 text-sm text-[#2D2420] max-w-md">
                        <p className="line-clamp-2">{item.testimonial || '-'}</p>
                      </td>
                      <td className="py-3 px-4 text-center text-[#2D2420] font-medium">{item.rating || 0}/5</td>
                      <td className="py-3 px-4 text-center">
                        <Switch
                          checked={item.is_approved}
                          onCheckedChange={(checked) => updateTestimonialFlag(item.id, { is_approved: checked })}
                          disabled={testimonialUpdatingId === item.id}
                        />
                      </td>
                      <td className="py-3 px-4 text-center">
                        <Switch
                          checked={item.is_featured}
                          onCheckedChange={(checked) => updateTestimonialFlag(item.id, { is_featured: checked })}
                          disabled={testimonialUpdatingId === item.id}
                        />
                      </td>
                      <td className="py-3 px-4 text-center">
                        <Switch
                          checked={item.is_active}
                          onCheckedChange={(checked) => updateTestimonialFlag(item.id, { is_active: checked })}
                          disabled={testimonialUpdatingId === item.id}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
